/**
 * Feature flags for runtime toggles that don't ship through the
 * server-side ``system_config`` registry. Today this is purely a
 * URLSearchParams reader — flip a flag by appending the parameter to
 * any URL, no deployment required.
 *
 * The Konva canvas migration is now DEFAULT-ON. ``?canvas=svg`` opts
 * a single session OUT to the legacy SVG-backed ``Canvas.tsx`` for
 * emergency rollback during the soak window. ``?canvas=konva`` is
 * still recognised (no-op vs. default) so existing share links from
 * the opt-in era keep working.
 *
 * Why URL-param and not server-side config:
 *   * The migration toggle is a developer / drafter escape hatch for
 *     incremental rollout, not a per-tenant policy. Server-side
 *     config exists for the latter (``system_config`` rows under
 *     ``backend/config_db.py``).
 *   * Per-session opt-out lets a drafter who hits a Konva regression
 *     keep working on the SVG canvas while the issue is filed.
 *
 * SSR safety: ``window.location`` is window-scoped; ``typeof window``
 * guard keeps this import-safe in any future SSR / test pre-render
 * environment that doesn't carry a ``window``.
 */

function _readCanvasParam(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('canvas');
  } catch {
    return null;
  }
}

export const FEATURES = {
  /**
   * Konva-backed drawing canvas. When true, ``DrawingPage`` mounts
   * ``KonvaCanvas`` instead of ``Canvas``. Both components share the
   * same prop interface (``{matterId, figure}``) and pull every
   * other piece of state from ``useDrawingStore``, so the consumer-
   * side switch is a one-line change.
   *
   * Default-on as of the chunk-3-followup soak. ``?canvas=svg``
   * forces the legacy SVG canvas; any other value (including
   * ``konva`` and absent) lands on Konva.
   *
   * Migration chunks:
   *   1. Read-only render parity (raster + chips, no interactions).
   *   2. Tool implementation — cursor/numeral/arrow/text/lasso.
   *   3. Vector-mode absorption + display normalisation.
   *   4-6. Persistence parity, cleanup, Canvas.tsx removal —
   *        post-soak.
   */
  USE_KONVA_CANVAS: _readCanvasParam() !== 'svg',
} as const;

export type FeatureKey = keyof typeof FEATURES;
